import React, { useState, useEffect } from 'react';
import { colors } from '../../../Colors';
import { clipAudioApi } from '../../../services/api';
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress
} from '@mui/material';
import { MusicNote, ArrowForward } from '@mui/icons-material';

function Step1ThemePrompt({ projectId, projectData, onSongGenerated, onNextStep, onRefreshProject }) {
  const [themePrompt, setThemePrompt] = useState(() => {
    const theme = projectData?.lyric?.theme || projectData?.theme;
    return typeof theme === 'string' ? theme : (theme?.theme || '');
  });
  const [isGenerating, setIsGenerating] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [generatedSong, setGeneratedSong] = useState(null);

  // Auto-generate song if lyrics already exist from project creation
  useEffect(() => {
    const lyrics = projectData?.lyrics || projectData?.lyric?.lyric;
    if (lyrics && !generatedSong) {
      const existingSong = {
        id: Date.now(),
        title: `Song based on: ${(() => {
          const theme = projectData.lyric?.theme || projectData.theme;
          return typeof theme === 'string' ? theme : (theme?.theme || '');
        })()}`,
        lyrics: lyrics,
        theme: (() => {
          const theme = projectData.lyric?.theme || projectData.theme;
          return typeof theme === 'string' ? theme : (theme?.theme || '');
        })(),
        timestamp: new Date().toISOString()
      };
      
      setGeneratedSong(existingSong);
      // Don't automatically call onSongGenerated - let user control the flow
    }
  }, [projectData, generatedSong]);

  const handleGenerate = async () => {
    if (!String(themePrompt).trim()) return;

    setIsGenerating(true);
    
    try {
      const result = await clipAudioApi.executeStep(projectId, projectData.current_step);
      console.log('Generation successful:', result);
      // Refresh project data to get latest state
      if (onRefreshProject) {
        onRefreshProject();
      }
    } catch (error) {
      console.error('Error generating lyrics:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleValidate = async () => {
    setIsValidating(true);
    
    try {
      const result = await clipAudioApi.validateStep(projectId, projectData.current_step);
      console.log('Validation successful:', result);
      // Refresh project data to get latest state
      if (onRefreshProject) {
        onRefreshProject();
      }
      // Handle successful validation
      if (onNextStep) {
        onNextStep();
      }
    } catch (error) {
      console.error('Error validating step:', error);
    } finally {
      setIsValidating(false);
    }
  };

  return (
    <Box sx={{ p: 2.5 }}>
      <Card sx={{ mb: 2.5, borderRadius: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h4" component="h3" sx={{ 
            textAlign: 'center', 
            mb: 2, 
            fontWeight: 600,
            color: '#2c3e50'
          }}>
            🎵 Étape 1: Génération des Paroles
          </Typography>
          
          {/* Display Theme */}
          {(() => {
            const theme = projectData?.lyric?.theme || projectData?.theme;
            const themeText = typeof theme === 'string' ? theme : (theme?.theme || '');
            return themeText && (
              <Alert severity="info" sx={{ mb: 2.5, py: 1 }}>
                <Typography variant="body1" sx={{ color: '#2c3e50', fontWeight: 600 }}>
                  🎯 Thème: {themeText}
                </Typography>
              </Alert>
            );
          })()}
          
          <Box>
            <TextField
              fullWidth
              label="Décrivez votre chanson"
              placeholder="Ex: Une chanson d'amour romantique sur une plage au coucher du soleil..."
              value={themePrompt}
              onChange={(e) => setThemePrompt(e.target.value)}
              multiline
              rows={4}
              variant="outlined"
              sx={{ mb: 2.5 }}
            />

            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
              <Button
                variant="contained"
                onClick={handleGenerate}
                disabled={isGenerating}
                startIcon={isGenerating ? <CircularProgress size={20} /> : <MusicNote />}
                sx={{
                  background: 'linear-gradient(135deg, #3498DB 0%, #2980B9 100%)',
                  borderRadius: '50px',
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  minWidth: '150px',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #2980B9 0%, #1F618D 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px rgba(52, 152, 219, 0.4)'
                  },
                  '&:disabled': {
                    background: '#cbd5e0',
                    transform: 'none',
                    boxShadow: 'none'
                  }
                }}
              >
                {isGenerating ? 'Génération...' : 'Générer'}
              </Button>
              
              <Button
                variant="contained"
                onClick={handleValidate}
                disabled={isValidating || projectData?.step_status !== "completed"}
                startIcon={isValidating ? <CircularProgress size={20} /> : <ArrowForward />}
                sx={{
                  background: 'linear-gradient(135deg, #27AE60 0%, #2ECC71 100%)',
                  borderRadius: '50px',
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  minWidth: '150px',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #229954 0%, #27AE60 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px rgba(39, 174, 96, 0.4)'
                  },
                  '&:disabled': {
                    background: '#cbd5e0',
                    transform: 'none',
                    boxShadow: 'none'
                  }
                }}
              >
                {isValidating ? 'Validation...' : 'Valider'}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Display Lyrics when step is completed */}
      {projectData?.step_status === "completed" && projectData?.lyric?.lyric && (
        <Card sx={{ mt: 2.5, borderRadius: 3, bgcolor: '#F0F8FF' }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h5" component="h4" sx={{ 
              textAlign: 'center', 
              mb: 2, 
              fontWeight: 'bold',
              color: '#2C3E50',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1
            }}>
              <MusicNote color="primary" />
              Paroles générées
            </Typography>
            
            <Box sx={{ 
              bgcolor: '#FFFFFF', 
              borderRadius: 2, 
              p: 2.5,
              border: '1px solid #E1E8ED',
              whiteSpace: 'pre-line',
              fontFamily: 'monospace',
              fontSize: '0.95rem',
              lineHeight: 1.6,
              mb: 2.5
            }}>
              {projectData.lyric.lyric}
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="contained"
                onClick={() => handleValidate()}
                startIcon={<ArrowForward />}
                sx={{
                  background: 'linear-gradient(135deg, #27AE60 0%, #2ECC71 100%)',
                  borderRadius: '50px',
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  minWidth: '180px',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #229954 0%, #27AE60 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px rgba(39, 174, 96, 0.4)'
                  }
                }}
              >
                Continuer vers l'Étape 2
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}


    </Box>
  );
}

export default Step1ThemePrompt;
